import { InputGroup, Input, InputRightElement, Button } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { LoanContext } from "providers/loan";
import { useContext, useRef, useState } from "react";
import { api } from "utils/api";
import { FieldValue } from "utils/types";

interface InputEncryptedProps {
    fieldId: string;
    label: string;
    setValue: (fieldId: string, value: FieldValue) => void;
    placeholder: string;
    value?: string;
}

export function InputEncrypted({
    fieldId,
    label,
    setValue,
    placeholder,
    value,
}: InputEncryptedProps) {
    const { loan, cacheKey } = useContext(LoanContext);

    const defaultState = "********";

    const initialValue = useRef<string | undefined | null>(value);

    const [inputValue, setInputValue] = useState<string>(
        !!initialValue.current ? defaultState : ""
    );

    const [showEncryptedValue, setShowEncryptedValue] = useState(
        !!initialValue.current ? false : true
    );

    const {
        data: decryptedValue,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: ["encryptedValue", loan!.id, fieldId, cacheKey],
        queryFn: () => fetchEncryptedValue({ loanId: loan!.id, fieldId }),
        enabled: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    function handleInputChange(ev: React.ChangeEvent<HTMLInputElement>) {
        let val = ev.target.value;
        if (label === "SSN") {
            val = val.replace(/\D/g, '').slice(0, 9);
        }
        setInputValue(val);
        setValue(fieldId, val);
    }

    async function handleShowEncryptedValue(show: boolean) {
        if (show) {
            if (!decryptedValue) {
                const { data } = await refetch();
                setInputValue(data ?? "");
            } else {
                setInputValue(decryptedValue);
            }
            setShowEncryptedValue(true);
        } else {
            setInputValue(defaultState);
            setShowEncryptedValue(false);
        }
    }

    function showDecryptButton() {
        return !!initialValue.current && initialValue.current === value;
    }

    return (
        <InputGroup>
            <Input
                pr="4.5rem"
                type="text"
                data-cy={label}
                value={inputValue}
                onChange={(ev) => handleInputChange(ev)}
                onPaste={ev => {
                    if (label === "SSN") {
                        let pasted = ev.clipboardData.getData('Text').replace(/\D/g, '').slice(0, 9);
                        ev.preventDefault();
                        setInputValue(pasted);
                        setValue(fieldId, pasted);
                    }
                }}
                placeholder={placeholder}
                variant="primary"
                isReadOnly={!showEncryptedValue}
            />
            {showDecryptButton() && (
                <InputRightElement width="4.5rem">
                    <Button
                        border={0}
                        color={"gray.900"}
                        backgroundColor={"gray.50"}
                        h="1.75rem"
                        size="sm"
                        onClick={() =>
                            handleShowEncryptedValue(!showEncryptedValue)
                        }
                        isLoading={isFetching}
                        _hover={{
                            backgroundColor: "gray.200",
                        }}
                    >
                        {showEncryptedValue ? "Hide" : "Show"}
                    </Button>
                </InputRightElement>
            )}
        </InputGroup>
    );
}

async function fetchEncryptedValue({
    loanId,
    fieldId,
}: {
    loanId: string;
    fieldId: string;
}): Promise<string> {
    const response = await api.get(
        `/loans/${loanId}/fields/${fieldId}/decrypt`
    );
    return response.data.value;
}
